import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { UserCheck, AlertTriangle, Loader2, Search, Link2, X, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Conexões atuais da pessoa, com opção de remover cada líder (ex.: equipe com 2 líderes → manter só 1)
const CurrentConnections = ({ person, onChanged }) => {
  const [busy, setBusy] = useState(null);
  const [removidos, setRemovidos] = useState([]);
  const conexoes = (person?.conexoes || [])
    .filter(c => c.tipo !== "lider")
    .map(c => ({ ...c, lideres: (c.lideres || []).filter(l => !removidos.includes(`${c.origem_id}-${l.id}`)) }))
    .filter(c => c.lideres.length > 0);
  if (conexoes.length === 0) return null;

  const remover = async (c, l) => {
    const nome = (l.nome || "").split(" ")[0];
    if (!window.confirm(`Remover ${nome} como conexão de ${person.nome_completo.split(" ")[0]}${c.tipo === "direta" ? "" : ` (${c.origem_nome})`}?`)) return;
    setBusy(`${c.origem_id}-${l.id}`);
    try {
      if (c.tipo === "direta") await axios.delete(`${API}/connections/${c.id}`);
      else await axios.post(`${API}/connections/exclusions`, { pessoa_id: person.id, origem_id: c.origem_id, lider_pessoa_id: l.id });
      toast.success("Conexão removida");
      setRemovidos(r => [...r, `${c.origem_id}-${l.id}`]);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao remover conexão");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-1.5" data-testid="current-connections">
      <p className="text-xs font-medium text-muted-foreground flex items-center gap-1"><Users className="h-3.5 w-3.5" /> Conexões atuais</p>
      {conexoes.map(c => (c.lideres || []).map(l => (
        <div key={`${c.origem_id}-${l.id}`} className="flex items-center gap-2 p-2 rounded-lg border bg-muted/30" data-testid={`current-conn-${c.origem_id}-${l.id}`}>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{l.nome}</p>
            <p className="text-[11px] text-muted-foreground truncate">{c.tipo === "direta" ? "Conexão direta" : c.origem_nome}</p>
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive shrink-0" title="Remover esta conexão" disabled={busy === `${c.origem_id}-${l.id}`} onClick={() => remover(c, l)} data-testid={`remove-conn-${c.origem_id}-${l.id}`}>
            {busy === `${c.origem_id}-${l.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
          </Button>
        </div>
      )))}
    </div>
  );
};

export const waLink = (telefone, text) => {
  const digits = (telefone || "").replace(/\D/g, "");
  if (!digits) return null;
  const full = digits.length <= 11 ? `55${digits}` : digits;
  return `https://wa.me/${full}${text ? `?text=${encodeURIComponent(text)}` : ""}`;
};

export const renderTemplate = (texto, vars) =>
  (texto || "").replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined && vars[k] !== null && vars[k] !== "" ? vars[k] : m));

const SEXO_LABEL = { M: "homem", F: "mulher" };

// Escolhe a pessoa que será a CONEXÃO de "person" (vincular ou alterar)
export const ConnectPersonModal = ({ open, onOpenChange, person, summary, onConnected }) => {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [configurado, setConfigurado] = useState(true);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  const jaConectado = !!person?.conectado;

  useEffect(() => {
    if (!open) { setQ(""); setSelected(null); return; }
    setLoading(true);
    axios.get(`${API}/connections/mentors`, { params: { q, sexo: person?.sexo || undefined } })
      .then(r => { setItems(r.data.items || []); setConfigurado(r.data.configurado !== false); })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [open, q, person?.sexo]);

  const alertaSexo = selected && person?.sexo && selected.sexo && selected.sexo !== person.sexo;

  const confirmar = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await axios.post(`${API}/connections`, {
        pessoa_id: person.id, origem: "manual", lider_pessoa_id: selected.id, substituir: jaConectado,
      });
      toast.success(`${person.nome_completo.split(" ")[0]} agora está conectado(a) com ${selected.nome_completo.split(" ")[0]}`);
      onConnected?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao vincular conexão");
    } finally {
      setSaving(false);
    }
  };

  if (!person) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden w-[calc(100vw-1rem)] sm:w-full p-4 sm:p-6 [&>*]:min-w-0" data-testid="connect-person-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Link2 className="h-5 w-5 text-primary" /> {jaConectado ? "Alterar conexão" : "Vincular conexão"}</DialogTitle>
          <DialogDescription>
            Escolha quem será a conexão de <strong>{person.nome_completo}</strong>.
          </DialogDescription>
        </DialogHeader>

        {jaConectado && <CurrentConnections person={person} onChanged={() => onConnected?.()} />}

        {!configurado && (
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800" data-testid="connect-not-configured">
            Nenhuma etapa do Trilho do Reino do tipo "Pode ser conexão" está configurada. Crie a etapa e marque-a nas pessoas que podem ser conexão de outras.
          </div>
        )}

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar pessoa que pode ser conexão..." className="pl-9" data-testid="mentor-search-input" />
        </div>

        <div className="max-h-[45vh] overflow-y-auto space-y-1.5 pr-1" data-testid="mentor-picker">
          {loading ? (
            <p className="text-sm text-muted-foreground flex items-center gap-2 py-4 justify-center"><Loader2 className="h-4 w-4 animate-spin" /> Carregando...</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">Nenhuma pessoa encontrada</p>
          ) : items.filter(m => m.id !== person.id).map(m => {
            const sel = selected?.id === m.id;
            const mesmoSexo = !person.sexo || !m.sexo || m.sexo === person.sexo;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => setSelected(m)}
                className={`w-full text-left flex items-center gap-3 p-2.5 rounded-xl border transition-colors ${sel ? "border-primary bg-primary/5" : "hover:bg-muted/50"}`}
                data-testid={`mentor-option-${m.id}`}
              >
                <div className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-semibold shrink-0 ${m.sexo === "F" ? "bg-pink-100 text-pink-700" : "bg-sky-100 text-sky-700"}`}>
                  {(m.nome_completo || "?")[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{m.nome_completo}</p>
                  <p className="text-xs text-muted-foreground">{m.conexoes} conexão(ões){m.telefone ? ` · ${m.telefone}` : ""}</p>
                </div>
                {!mesmoSexo && <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" title="Sexo diferente" />}
                {sel && <UserCheck className="h-4 w-4 text-primary shrink-0" />}
              </button>
            );
          })}
        </div>

        {alertaSexo && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800" data-testid="sex-alert">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <p>Atenção: {selected.nome_completo.split(" ")[0]} é {SEXO_LABEL[selected.sexo]} e {person.nome_completo.split(" ")[0]} é {SEXO_LABEL[person.sexo]}. O recomendado é conexão entre pessoas do mesmo sexo. Você pode continuar mesmo assim.</p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-2 pt-1">
          <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button className="flex-1" disabled={!selected || saving} onClick={confirmar} data-testid="connect-mark-manual-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Link2 className="h-4 w-4 mr-2" />}
            {jaConectado ? "Alterar conexão" : "Vincular conexão"}
          </Button>
        </div>
        {selected && <Badge variant="secondary" className="self-start text-[10px]">Conexão escolhida: {selected.nome_completo}</Badge>}
      </DialogContent>
    </Dialog>
  );
};

export default ConnectPersonModal;
